import * as React from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import { CaretUpDownIcon, PlusIcon } from '@phosphor-icons/react';
import { useOrganizationLogo } from '@/hooks/organization-config/useOrganizationLogo';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Product } from '@/components/common/semantic-icons';

interface TeamSwitcherProps {
  brandName: string;
  teams?: {
    name: string;
    logo: React.ElementType;
    plan: string;
  }[];
}

export function TeamSwitcher({ teams = [] }: TeamSwitcherProps) {
  const { isMobile } = useSidebar();
  const { logoUrl, organizationName } = useOrganizationLogo();
  const [_activeTeam, setActiveTeam] = React.useState(teams[0] || null);

  const menuButton = (
    <SidebarMenuButton
      size="lg"
      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
    >
      <div className="bg-white text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${organizationName} Logo`}
            className="size-6 rounded object-contain"
          />
        ) : (
          <Product className="size-4" />
        )}
      </div>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">Gently AI</span>
        <span className="truncate text-xs">{organizationName}</span>
      </div>
      {teams.length > 0 && <CaretUpDownIcon className="ml-auto" />}
    </SidebarMenuButton>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {teams.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{menuButton}</DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                Teams
              </DropdownMenuLabel>
              {teams.map((team, index) => (
                <DropdownMenuItem
                  key={team.name}
                  onClick={() => setActiveTeam(team)}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border">
                    <team.logo className="size-3.5 shrink-0" />
                  </div>
                  {team.name}
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 p-2">
                <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                  <PlusIcon className="size-4" />
                </div>
                <div className="text-muted-foreground font-medium">
                  Add team
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <RouterLink to="/home" className="cursor-pointer">
            {menuButton}
          </RouterLink>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
